import React, { useState } from "react";
import "./EmotionDisplay.css";
import useTranslation from '../hooks/useTranslation';

// Emoji map with Unicode
const EMOJI_MAP = {
  happy: "😊",
  happiness: "😊",
  sad: "😞",
  sadness: "😞",
  fear: "😱",
  angry: "😠",
  anger: "😠",
  disgust: "🤢",
  neutral: "😐"
};

const MENTAL_STATE_MAP = {
  depression: { emoji: "😞", color: "#6c757d", label: "Depression" },
  anxiety: { emoji: "😰", color: "#ff9800", label: "Anxiety" },
  ptsd: { emoji: "😓", color: "#9c27b0", label: "PTSD" },
  "bipolar disorder": { emoji: "🎭", color: "#673ab7", label: "Bipolar Disorder" },
  dementia: { emoji: "🧠", color: "#3f51b5", label: "Dementia" },
  "suicidal thoughts": { emoji: "🚨", color: "#f44336", label: "Suicidal Thoughts" },
  "violent outbursts": { emoji: "💥", color: "#e53935", label: "Violent Outbursts" },
  unknown: { emoji: "❓", color: "#757575", label: "Unknown" }
};

// Simple bar renderer
function Bar({ value }) {
  return (
    <div className="bar-bg">
      <div className="bar-fill" style={{ width: `${value}%` }} />
    </div>
  );
}

export default function EmotionResultBox({ result, user, onClose, onSaved }) {
  const user_name = user || localStorage.getItem("neuro_user_name");
  const [closing, setClosing] = useState(false);
  const top5 = result.top5 || [];
  const rawMentalState = result.mental_state?.toLowerCase() || "unknown";
  const mentalStateKey = Object.keys(MENTAL_STATE_MAP).find(
    key => rawMentalState.includes(key)
  ) || "unknown";
  const mentalState = MENTAL_STATE_MAP[mentalStateKey];

  const waveformMel = result?.PLOT?.waveform_mel || "";
  const emotionRadar = result?.PLOT?.emotions || "";

  const { t } = useTranslation();

  return (
    <div className="emotion-result-wrapper" style={{ transition: 'opacity 220ms ease, transform 220ms ease', opacity: closing ? 0 : 1, transform: closing ? 'translateY(-6px)' : 'none', position: 'relative' }}>
      {/* Close (X) button */}
      <button
        aria-label={t('Close report')}
        onClick={() => { setClosing(true); setTimeout(() => { if (typeof onClose === 'function') onClose(); }, 200); }}
        style={{ position: 'absolute', right: 8, top: 8, border: 'none', background: 'transparent', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
      >
        ×
      </button>
      <div className="emoji-top">
        <img src="/1.png" alt="" />

      </div>
      <div className="emotion-result-card" style={{ maxWidth: 1100 }}>
        <h2>{t('Hi,')} {user_name}!</h2>
        <p>{t('Here are the latest results from your self-reflections.')}</p>

        {/* Responsive two-column layout */}

        <div className="emotion-content-grid">

          <div className="emotion-visualization-section">
            {(waveformMel || emotionRadar) && (
              <div className="plot-gallery">
                {waveformMel && (
                  <div className="plot-image-wrapper">
                    <h3>{t('Audio Waveform & Mel-Spectrogram')}</h3>
                    <img
                      className="plot-image"
                      src={`data:image/png;base64,${waveformMel}`}
                      alt="Waveform + Mel"
                    />
                  </div>
                )}
                {emotionRadar && (
                  <div className="plot-image-wrapper">
                    <h3>{t('Emotion Radar')}</h3>
                    <img
                      className="plot-image"
                      src={`data:image/png;base64,${emotionRadar}`}
                      alt="Emotion Radar"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="emotion-analysis-section">
            <h3>{t('Emotional Analysis')}</h3>
            <div className="mental-state-block" style={{ borderLeft: `6px solid ${mentalState.color}` }}>
              <p className="mental-state" style={{ color: mentalState.color }}>
                {mentalState.emoji} <strong>{t('Mental State:')}</strong> {mentalState.label}
              </p>
              {mentalStateKey === "suicidal thoughts" && (
                <p className="alert-msg" style={{ color: "#d32f2f", fontSize: "14px" }}>
                  ⚠️ {t('If you\'re in crisis or need help, please reach out to a mental health professional or emergency support line.')}
                </p>
              )}
            </div>

            <div className="top5-title">{t('Top 5 chart')}</div>
            <div className="top5-row">
              <span className="emoji top5-main-emoji" style={{ fontSize: "52px" }}>
                {EMOJI_MAP[top5[0]?.label?.toLowerCase()] || "🙂"}
              </span>
              <div className="top5-chart">
                {top5.map((em) => (
                  <div className="top5-bar-row" key={em.label}>
                    <span className="top5-label">{em.label}</span>
                    <Bar value={parseFloat(em.value)} />
                    <span className="top5-value">{em.value}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Responsive subcategory blocks */}

            <div className="subcategory-grid">
              {top5.slice(0, 2).map((em, idx) => {
                const mainSub = idx === 0 ? result.sub1 : result.sub2;
                const altSub = idx === 0 ? result.sub1b : result.sub2b;
                if (!mainSub?.length && !altSub?.length) return null;
                return (
                  <div className="subcat-block" key={em.label}>
                    <div className="subcat-header">
                      <span className="emoji" style={{ fontSize: "38px" }}>
                        {EMOJI_MAP[em.label.toLowerCase()] || "🙂"}
                      </span>
                      <span className="subcat-title">
                        {em.label.charAt(0).toUpperCase() + em.label.slice(1)}
                      </span>
                    </div>
                    <div className="subcat-bar-grid">
                      <div>
                        {mainSub.map((sub, i) => (
                          <div className="subcategory-row" key={i}>
                            <span>{sub.label}</span>
                            <Bar value={sub.value} />
                          </div>
                        ))}
                      </div>
                      <div>
                        {altSub &&
                          altSub.map((sub, i) => (
                            <div className="subcategory-row" key={i}>
                              <span>{sub.label}</span>
                              <Bar value={sub.value} />
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {/* Subcategory blocks are displayed inline above */}
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button
            className="continue-btn"
            onClick={async () => {
              try {
                const { saveDailyReport } = await import('../components/api'); // adjust path if needed
                const stored = localStorage.getItem('neuro_user_id');
                const user_id = stored && stored.length
                  ? stored
                  : (process.env.REACT_APP_DEV_FALLBACK_USER_ID || null); // no hardcoded fallback in production

                if (!user_id) {
                  console.error('No user ID found - user must be logged in');
                  alert('Please log in to save reports');
                  return;
                }

                const date = new Date().toISOString().slice(0, 10);
                // build emotions from top5 (safe parsing)
                const findPercent = (label) => {
                  try {
                    const e = top5.find(x => (x.label || '').toLowerCase() === label);
                    return e ? parseFloat(e.value) : 0;
                  } catch { return 0; }
                };
                const emotions = {
                  surprise: findPercent('surprise'),
                  fear: findPercent('fear'),
                  sad: findPercent('sad'),
                  angry: findPercent('angry'),
                  disgust: findPercent('disgust'),
                  happy: findPercent('happy')
                };
                const res = await saveDailyReport({ user_id, date, emotions, meta: { source: 'manual_save' } });
                // Expect res to be { status_code: 200, ... } or similar; handle both shapes
                const ok = (res && (res.status_code === 200 || res.status === 200)) || (res && res.message === 'Report saved');
                if (ok) {
                  // Fade out and notify parent to show success message
                  setClosing(true);
                  setTimeout(() => {
                    if (typeof onSaved === 'function') onSaved();
                  }, 180);
                } else {
                  console.error('saveDailyReport response:', res);
                  alert('Failed to save to dashboard');
                }
              } catch (e) {
                console.error('saveDailyReport error', e);
                alert('Failed to save to dashboard');
              }
            }}
          >
            Save to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}